import type { QueryClient } from '@tanstack/react-query';

/**
 * Milliseconds until the next sync is due, given when the last one
 * completed. Never synced (or unparsable) → due now. Clock skew or a future
 * timestamp → clamped to a full interval.
 */
export function syncDelayMs(
  lastSyncedAt: string | null | undefined,
  now: number,
  intervalMs: number,
): number {
  if (!lastSyncedAt) return 0;
  const last = Date.parse(lastSyncedAt);
  if (Number.isNaN(last)) return 0;
  const elapsed = now - last;
  if (elapsed < 0) return intervalMs;
  return Math.max(0, intervalMs - elapsed);
}

/**
 * Resolves once no query has been fetching for `quietMs`. Primary data loads
 * go through TanStack Query, so a quiet cache is the app's "network idle".
 * Returns a cancel function; a cancelled wait never resolves.
 */
export function waitForNetworkIdle(
  queryClient: QueryClient,
  quietMs: number,
): { promise: Promise<void>; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let unsubscribe = () => {};
  let settled = false;

  const promise = new Promise<void>((resolve) => {
    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve();
    };
    const arm = () => {
      clearTimeout(timer);
      if (queryClient.isFetching() === 0) {
        timer = setTimeout(finish, quietMs);
      }
    };
    unsubscribe = queryClient.getQueryCache().subscribe(arm);
    arm();
  });

  const cancel = () => {
    settled = true;
    clearTimeout(timer);
    unsubscribe();
  };

  return { promise, cancel };
}
